import React, { useEffect, useState, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useScreenInit } from './useScreenInit.js';
import { PresentationFrame } from './components/PresentationFrame';
import { MinervaLogo } from './components/MinervaLogo';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Slide1Executive } from './slides/Slide1Executive';
import { SlideJourney } from './slides/SlideJourney';
import { Slide2Strategy } from './slides/Slide2Strategy';
import { Slide3Engagement } from './slides/Slide3Engagement';
import { SlideBenchmark } from './slides/SlideBenchmark';
import { SlideLeadQuality } from './slides/SlideLeadQuality';
import { SlideQrAisle } from './slides/SlideQrAisle';
import { Slide6Market } from './slides/Slide6Market';
import { Slide7Perception } from './slides/Slide7Perception';
import { Slide13Learnings } from './slides/Slide13Learnings';
import { Slide17Improvements } from './slides/Slide17Improvements';
import { Slide20Workflow } from './slides/Slide20Workflow';
import { Slide21Final } from './slides/Slide21Final';
const SLIDES = [Slide1Executive, SlideJourney, Slide2Strategy, Slide3Engagement, SlideBenchmark, SlideLeadQuality, SlideQrAisle, Slide6Market, Slide7Perception, Slide13Learnings, Slide17Improvements, Slide20Workflow, Slide21Final];
export function App() {
  const screenInit = useScreenInit();
  const [currentIndex, setCurrentIndex] = useState(typeof screenInit?.currentIndex === 'number' ? screenInit.currentIndex : 0);
  const nextSlide = () => {
    setCurrentIndex(prev => prev < SLIDES.length - 1 ? prev + 1 : prev);
  };
  const prevSlide = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : prev);
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const CurrentSlideComponent = SLIDES[currentIndex];
  return <PresentationFrame data-id="element-0">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 px-10 py-4 flex justify-between items-center z-50 pointer-events-none" data-id="element-1">
        <MinervaLogo className="h-9" data-id="element-2" />
        <div className="text-sm font-medium text-slate-400" data-id="element-3">
          SNMMI Congress Results • {currentIndex + 1} / {SLIDES.length}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 relative overflow-y-auto overflow-x-hidden show-scroll pt-16 pb-10 px-14" data-id="element-4">
        <AnimatePresence mode="wait" data-id="element-5">
          <motion.div key={currentIndex} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.5,
          ease: 'easeOut'
        }} className="w-full min-h-full flex flex-col" data-id="element-6">
            <CurrentSlideComponent data-id="element-7" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Side Navigation Arrows */}
      <button onClick={prevSlide} disabled={currentIndex === 0} aria-label="Previous slide" className={`absolute left-4 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/90 backdrop-blur shadow-md border border-slate-100 transition-all z-50 ${currentIndex === 0 ? 'opacity-0 pointer-events-none' : 'hover:bg-white hover:scale-105 text-slate-600'}`} data-id="element-8">
        <ChevronLeft className="w-5 h-5" data-id="element-9" />
      </button>
      <button onClick={nextSlide} disabled={currentIndex === SLIDES.length - 1} aria-label="Next slide" className={`absolute right-4 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-minerva-blue/95 backdrop-blur shadow-md text-white transition-all z-50 ${currentIndex === SLIDES.length - 1 ? 'opacity-0 pointer-events-none' : 'hover:bg-minerva-dark hover:scale-105'}`} data-id="element-10">
        <ChevronRight className="w-5 h-5" data-id="element-11" />
      </button>

      {/* Bottom segmented progress bar */}
      <div className="absolute bottom-0 left-0 right-0 flex gap-1 px-3 pb-2 z-50" data-id="element-12">
        {SLIDES.map((_, i) => <button key={i} onClick={() => setCurrentIndex(i)} aria-label={`Go to slide ${i + 1}`} className="group flex-1 py-2 flex items-end" data-id="element-13">
            <span className={`block w-full h-1.5 rounded-full transition-all ${i === currentIndex ? 'bg-minerva-blue' : 'bg-slate-200 group-hover:bg-slate-300'}`} data-id="element-14" />
          </button>)}
      </div>
    </PresentationFrame>;
}